/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Group, Text, rem, useMantineTheme, type GroupProps } from '@mantine/core';
import { RiInformation2Line } from '@remixicon/react';
import type { ReactNode } from 'react';

import { colorToken, variablesToken } from '@fai/design-tokens';
import { WarningOutlineIcon } from '@fai/svgr';

import type { MessageBoxProps } from './model';
import { MessageBoxVariant } from './variants';
import { TextVariants } from '../../../components/typography/variants';

const messageBoxStyles: {
  [key in MessageBoxVariant]: {
    icon: ReactNode;
    boxStyles: GroupProps;
  };
} = {
  [MessageBoxVariant.Warning]: {
    icon: <RiInformation2Line size={24} />,
    boxStyles: {
      bg: colorToken.color.bg.warning.default.base,
      c: colorToken.color.fg.warning.strong,
      style: {
        borderRadius: variablesToken.radius.md,
        borderLeft: `${rem(8)} solid ${colorToken.color.fg.warning.default.base}`,
      },
    },
  },
  [MessageBoxVariant.Danger]: {
    icon: <WarningOutlineIcon size={24} />,
    boxStyles: {
      bg: colorToken.color.bg.danger.default.base,
      c: colorToken.color.fg.danger.strong,
      style: {
        borderRadius: variablesToken.radius.md,
        borderLeft: `${rem(8)} solid ${colorToken.color.fg.danger.default.base}`,
      },
    },
  },
  [MessageBoxVariant.Success]: {
    icon: <RiInformation2Line size={24} />,
    boxStyles: {
      bg: colorToken.color.bg.success.default.base,
      c: colorToken.color.fg.success.strong,
      style: {
        borderRadius: variablesToken.radius.md,
        borderLeft: `${rem(8)} solid ${colorToken.color.fg.success.default.base}`,
      },
    },
  },
  [MessageBoxVariant.InformativeCloud]: {
    icon: <RiInformation2Line size={24} />,
    boxStyles: {
      bg: colorToken.color.bg.cloudblue.default.base,
      c: colorToken.color.fg.cloudblue.strong,
      style: {
        borderRadius: variablesToken.radius.md,
        borderLeft: `${rem(8)} solid ${colorToken.color.fg.cloudblue.default.base}`,
      },
    },
  },
  [MessageBoxVariant.InformativeApi]: {
    icon: <RiInformation2Line size={24} />,
    boxStyles: {
      bg: colorToken.color.bg.apiviolet.default.base,
      c: colorToken.color.fg.apiviolet.strong,
      style: {
        borderRadius: variablesToken.radius.md,
        borderLeft: `${rem(8)} solid ${colorToken.color.fg.apiviolet.default.base}`,
      },
    },
  },
};

export default function MessageBox({ variant, children, ...props }: MessageBoxProps) {
  const theme = useMantineTheme();
  const { icon, boxStyles } = messageBoxStyles[variant];

  return (
    <Group gap={theme.pf.layout['2xs']} p={theme.pf.layout['2xs']} {...boxStyles} {...props}>
      {icon}

      <Text
        component="div"
        style={{ whiteSpace: 'pre-wrap' }}
        variant={TextVariants.BodyMd}
        w="calc(100% - 2.25rem)"
      >
        {children}
      </Text>
    </Group>
  );
}

MessageBox.displayName = 'MessageBox';
